import React from 'react';
import { FiArrowLeft, FiCamera, FiTrash2, FiCheck, FiEye, FiX, FiStar } from 'react-icons/fi';
import { MdOutlineSell, MdOutlineDrafts, MdOutlinePending } from 'react-icons/md';
import MobileBottomNav from '../../../components/layout/MobileBottomNav';
import Button from '../../../components/common/Button';
import Badge from '../../../components/common/Badge';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import { IMAGE_BASE_URL } from '../../../services/api';

const MobileEditListing = ({
  listing,
  form,
  specializedData,
  categories,
  existingImages,
  newImages,
  error,
  submitting,
  categoryType,
  conditionOptions,
  conditionConfig,
  isListingVisibleToBuyers,
  onBack,
  onSubmit,
  onFieldChange,
  onSpecializedDataChange,
  onImageSelect,
  onRemoveExistingImage,
  onRemoveNewImage,
  onQuickStatusUpdate,
  onDeleteListing,
  renderSpecializedForm
}) => {
  const totalImages = existingImages.length + newImages.length;

  const statusConfig = {
    active: { label: 'Active', color: 'text-green-800', bgColor: 'bg-green-100', icon: FiEye },
    sold: { label: 'Sold', color: 'text-red-800', bgColor: 'bg-red-100', icon: MdOutlineSell },
    draft: { label: 'Draft', color: 'text-gray-800', bgColor: 'bg-gray-100', icon: MdOutlineDrafts },
    pending: { label: 'Pending', color: 'text-yellow-800', bgColor: 'bg-yellow-100', icon: MdOutlinePending },
  };

  const currentStatus = statusConfig[form.status] || statusConfig.active;

  // Handle status button clicks - directly call the parent handler which uses the hook
  const handleStatusClick = (status) => {
    if (status === 'active' && form.status !== 'active') {
      onQuickStatusUpdate('active');
    } else if (status === 'sold' && form.status !== 'sold') {
      onQuickStatusUpdate('sold');
    } else if (status === 'draft' && form.status !== 'draft') {
      onQuickStatusUpdate('draft');
    }
  };

  return (
    <div className="lg:hidden min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <Header />
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100"
              disabled={submitting}
            >
              <FiArrowLeft className="w-5 h-5 text-black" />
            </button>
            
            <h1 className="text-lg font-bold text-black">Edit Listing</h1>
            
            <div className="w-10"></div>
          </div>
        </div>
      </header>

      <div className="px-4 py-4 pb-24">
        {/* Visibility Status */}
        {isListingVisibleToBuyers ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2">
              <FiCheck className="w-5 h-5 text-green-600" />
              <span className="text-green-700 font-medium">Visible to buyers</span>
            </div>
          </div>
        ) : (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2">
              <FiEye className="w-5 h-5 text-yellow-600" />
              <span className="text-yellow-700 font-medium">
                {listing.moderation_status !== 'approved' ? 'Awaiting Approval' : 'Not Active'}
              </span>
            </div>
          </div>
        )}

        {/* Status Card */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <h3 className="font-bold text-black mb-2">Current Status</h3>
          <div className="flex items-center justify-between mb-3">
            <Badge type="status" status={form.status} size="medium" />
            <Badge type="moderation" status={listing.moderation_status} />
          </div>
          
          {/* Status Action Buttons - All three status options */}
          <div className="space-y-2">
            {/* Active Button */}
            {form.status !== 'active' && (
              <button
                onClick={() => handleStatusClick('active')}
                disabled={submitting}
                className="w-full flex items-center justify-between p-3 border border-green-200 rounded-lg hover:bg-green-50 transition"
              >
                <div className="flex items-center gap-2">
                  <FiEye className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-gray-700">Make Active</span>
                </div>
                <span className="text-sm text-green-600">Click to activate</span>
              </button>
            )}
            
            {/* Sold Button */}
            {form.status !== 'sold' && (
              <button
                onClick={() => handleStatusClick('sold')}
                disabled={submitting}
                className="w-full flex items-center justify-between p-3 border border-red-200 rounded-lg hover:bg-red-50 transition"
              >
                <div className="flex items-center gap-2">
                  <MdOutlineSell className="w-5 h-5 text-red-600" />
                  <span className="font-medium text-gray-700">Mark as Sold</span>
                </div>
                <span className="text-sm text-red-600">Click to mark sold</span>
              </button>
            )}
            
            {/* Draft Button */}
            {form.status !== 'draft' && (
              <button
                onClick={() => handleStatusClick('draft')}
                disabled={submitting}
                className="w-full flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
              >
                <div className="flex items-center gap-2">
                  <MdOutlineDrafts className="w-5 h-5 text-gray-600" />
                  <span className="font-medium text-gray-700">Save as Draft</span>
                </div>
                <span className="text-sm text-gray-600">Click to draft</span>
              </button>
            )}
          </div>
        </div>

        {/* Basic Info Form */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <h3 className="font-bold text-black mb-3">Basic Information</h3>
          
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title *
              </label>
              <input
                type="text"
                placeholder="Listing title"
                value={form.title}
                onChange={(e) => onFieldChange('title', e.target.value)}
                disabled={submitting}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                placeholder="Describe your listing..."
                value={form.description}
                onChange={(e) => onFieldChange('description', e.target.value)}
                rows={3}
                disabled={submitting}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Price *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500">₦</span>
                </div>
                <input
                  type="number"
                  placeholder="0.00"
                  value={form.price}
                  onChange={(e) => onFieldChange('price', e.target.value)}
                  disabled={submitting}
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">Negotiable</span>
              <button
                onClick={() => onFieldChange('is_negotiable', !form.is_negotiable)}
                disabled={submitting}
                className="relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                style={{
                  backgroundColor: form.is_negotiable ? '#10b981' : '#d1d5db'
                }}
              >
                <span
                  className={`inline-block w-4 h-4 transform transition-transform bg-white rounded-full ${
                    form.is_negotiable ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category *
              </label>
              <select
                value={form.category_id}
                onChange={(e) => onFieldChange('category_id', e.target.value)}
                disabled={submitting}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="">Select category</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Condition
              </label>
              <select
                value={form.condition}
                onChange={(e) => onFieldChange('condition', e.target.value)}
                disabled={submitting}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                {conditionOptions.map(option => (
                  <option key={option.id} value={option.value}>
                    {option.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Specialized Form */}
        {categoryType && (
          <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
            <h3 className="font-bold text-black mb-3">
              {categoryType.charAt(0).toUpperCase() + categoryType.slice(1)} Details
            </h3>
            {renderSpecializedForm()}
          </div>
        )}

        {/* Photos */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-black">Photos</h3>
            <span className="text-sm text-gray-500">{totalImages}/10</span>
          </div>

          {/* Existing Images */}
          {existingImages.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Current Photos</h4>
              <div className="grid grid-cols-3 gap-2">
                {existingImages.map((image) => (
                  <div key={image.image_id} className="relative">
                    <img
                      src={`${IMAGE_BASE_URL}${image.image_url}`}
                      alt="Listing"
                      className="w-full h-24 object-cover rounded-lg"
                    />
                    <button
                      onClick={() => onRemoveExistingImage(image.image_id)}
                      disabled={submitting}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                    >
                      <FiX className="w-3 h-3" />
                    </button>
                    {image.is_primary && (
                      <div className="absolute top-1 left-1 bg-blue-500 text-white px-1 rounded text-xs flex items-center gap-0.5">
                        <FiStar className="w-2 h-2" />
                        <span>Primary</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add New Images */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Add More Photos</h4>
            <div className="grid grid-cols-3 gap-2">
              {newImages.map((image, index) => (
                <div key={index} className="relative">
                  <img
                    src={image.uri}
                    alt="New"
                    className="w-full h-24 object-cover rounded-lg"
                  />
                  <button
                    onClick={() => onRemoveNewImage(index)}
                    disabled={submitting}
                    className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                  >
                    <FiX className="w-3 h-3" />
                  </button>
                </div>
              ))}
              
              {totalImages < 10 && (
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={onImageSelect}
                    className="hidden"
                    disabled={submitting}
                  />
                  <div className="w-full h-24 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center hover:border-orange-400 hover:bg-orange-50 transition">
                    <FiCamera className="w-6 h-6 text-gray-400 mb-1" />
                    <span className="text-xs text-gray-500">Add Photo</span>
                  </div>
                </label>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          {/* Update Button */}
          <Button
            onClick={onSubmit}
            disabled={submitting}
            loading={submitting}
            variant="primary"
            className="w-full"
            icon={<FiCheck className="w-5 h-5" />}
          >
            {submitting ? 'Updating...' : 'Update Listing'}
          </Button>

          {/* Cancel Button */}
          <Button
            onClick={onBack}
            disabled={submitting}
            variant="outline"
            className="w-full"
          >
            Cancel
          </Button>

          {/* Delete Button */}
          {/* <button
            onClick={onDeleteListing}
            disabled={submitting}
            className="w-full flex items-center justify-center gap-2 p-3 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition mt-4"
          >
            <FiTrash2 className="w-5 h-5" />
            Delete Listing
          </button>
          
          <p className="text-xs text-gray-500 text-center">
            Deleting a listing cannot be undone
          </p> */}
        </div>
      </div>

      <MobileBottomNav />
      <Footer />
    </div>
  );
};

export default MobileEditListing;